import { useEffect, useState, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Loader2,
  RefreshCw,
  Mail,
  Clock,
  TrendingUp,
  AlertCircle,
  Send,
  CheckCircle2,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  ComposedChart,
} from "recharts";

/**
 * Admin tab — Abandoned Checkouts.
 *
 * A "Pagos pendientes" surface that lists bookings stuck at
 * `status="pending_payment"` for more than the configured threshold
 * (default 30 minutes). Each row has a one-click "Enviar recordatorio"
 * button that fires a recovery email with a single-use discount code.
 *
 * Backend:
 *   - GET  /api/admin/abandoned-checkouts
 *   - POST /api/admin/abandoned-checkouts/{booking_id}/recover
 */
export default function AdminAbandonedCheckoutsTab() {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({}); // booking_id → bool
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    abandoned_total: 0,
    contacted_total: 0,
    recovered_total: 0,
    abandoned_threshold_min: 30,
    discount_pct: 10,
  });
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  const fetchAnalytics = useCallback(async (days) => {
    try {
      const { data } = await api.get(`/admin/abandoned-checkouts/analytics?days=${days}`);
      setAnalytics(data);
    } catch (_e) {
      /* non-critical */
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/abandoned-checkouts");
      setItems(data.items || []);
      setStats(data.stats || stats);
    } catch (_e) {
      toast.error("No pudimos cargar los checkouts abandonados");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAnalytics(analyticsDays);
  }, [analyticsDays, fetchAnalytics]);

  const handleRecover = async (bookingId) => {
    setActing((prev) => ({ ...prev, [bookingId]: true }));
    try {
      const { data } = await api.post(`/admin/abandoned-checkouts/${bookingId}/recover`);
      if (data?.ok) {
        toast.success(`Correo enviado · código ${data.code}`);
      } else {
        toast.warning(`Correo NO enviado pero código generado: ${data.code}`);
      }
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos enviar el recordatorio");
    } finally {
      setActing((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const recoveryRate =
    stats.contacted_total > 0
      ? Math.round((stats.recovered_total / stats.contacted_total) * 100)
      : 0;

  return (
    <div className="space-y-6" data-testid="admin-abandoned-tab">
      {/* Headline stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          testid="abandoned-stat-total"
          icon={Clock}
          colorClass="bg-amber-500"
          value={stats.abandoned_total}
          label={`Pagos pendientes > ${stats.abandoned_threshold_min}min`}
        />
        <StatCard
          testid="abandoned-stat-contacted"
          icon={Mail}
          colorClass="bg-blue-500"
          value={stats.contacted_total}
          label="Huéspedes contactados"
        />
        <StatCard
          testid="abandoned-stat-recovered"
          icon={TrendingUp}
          colorClass="bg-emerald-500"
          value={stats.recovered_total}
          label={`Recuperados (${recoveryRate}% conversión)`}
        />
      </div>

      {/* Offer banner */}
      <div
        className="rounded-xl border border-terracotta/20 bg-gradient-to-r from-terracotta/5 to-amber-50 p-4 flex items-start gap-3"
        data-testid="abandoned-offer-banner"
      >
        <div className="w-10 h-10 rounded-xl bg-terracotta flex items-center justify-center shrink-0">
          <Send className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm">
            Cada recordatorio incluye un código de <strong>{stats.discount_pct}% de descuento</strong> para
            el mismo vehículo.
          </p>
          <p className="text-xs text-neutral-600 mt-0.5">
            Es de un solo uso, dirigido al huésped, y vence en 7 días. La industria recupera el 10–15% de
            los checkouts abandonados con este patrón.
          </p>
        </div>
      </div>

      {/* Recovery analytics chart */}
      <div className="card-base" data-testid="abandoned-analytics-chart">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="font-heading font-semibold text-agave">Tendencia de recuperación</h3>
            <p className="text-xs text-neutral-500">
              Abandonados vs canjeados con código de descuento
            </p>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setAnalyticsDays(d)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  analyticsDays === d
                    ? "bg-agave text-white"
                    : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                }`}
                data-testid={`abandoned-analytics-range-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {analytics ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid="analytics-abandoned-total">
                <p className="text-xs text-neutral-600">Abandonados</p>
                <p className="text-xl font-bold text-amber-700">{analytics.stats.abandoned_total}</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid="analytics-contacted-total">
                <p className="text-xs text-neutral-600">Contactados</p>
                <p className="text-xl font-bold text-blue-700">{analytics.stats.contacted_total}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3" data-testid="analytics-recovered-total">
                <p className="text-xs text-neutral-600">Recuperados</p>
                <p className="text-xl font-bold text-emerald-700">
                  {analytics.stats.recovered_total}
                  <span className="text-xs font-medium ml-1">({analytics.stats.recovery_rate_pct}%)</span>
                </p>
              </div>
              <div className="bg-gradient-to-br from-terracotta/10 to-amber-50 border border-terracotta/20 rounded-lg p-3" data-testid="analytics-revenue">
                <p className="text-xs text-neutral-600 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Ingresos recuperados
                </p>
                <p className="text-xl font-bold text-terracotta">
                  ${analytics.stats.revenue_recovered.toLocaleString("es-MX", { maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={(analytics.series || []).map((d) => ({
                    date: d.date.slice(5),
                    Abandonados: d.abandoned,
                    Contactados: d.contacted,
                    Recuperados: d.recovered,
                    Ingresos: d.revenue,
                  }))}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="Abandonados" fill="#f59e0b" />
                  <Bar yAxisId="left" dataKey="Contactados" fill="#3b82f6" />
                  <Bar yAxisId="left" dataKey="Recuperados" fill="#10b981" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="Ingresos"
                    stroke="#8B3A2B"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="py-12 flex items-center justify-center text-neutral-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex justify-end">
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="abandoned-refresh"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          Refrescar
        </Button>
      </div>

      {/* Table */}
      <div className="card-base overflow-hidden p-0">
        {loading && items.length === 0 ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-neutral-600">No hay checkouts abandonados ahora mismo.</p>
            <p className="text-xs text-neutral-400 mt-1">
              Reservas con pago pendiente más de {stats.abandoned_threshold_min} minutos aparecerán aquí.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reserva</TableHead>
                  <TableHead>Huésped</TableHead>
                  <TableHead>Vehículo</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Edad</TableHead>
                  <TableHead>Recordatorio</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.booking_id} data-testid={`abandoned-row-${it.booking_id}`}>
                    <TableCell className="font-mono text-xs">{it.booking_id}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{it.guest_name}</p>
                        <p className="text-xs text-neutral-500 truncate max-w-[200px]">
                          {it.guest_email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {it.vehicle_label || "—"}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      ${it.total_price?.toLocaleString()} MXN
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {fmtAge(it.created_at)}
                    </TableCell>
                    <TableCell>
                      {it.recovery_email_sent_at ? (
                        <Badge
                          className="bg-blue-100 text-blue-800 gap-1"
                          title={`Código: ${it.recovery_code || ""}`}
                        >
                          <Mail className="w-3 h-3" />
                          Enviado · {fmtAge(it.recovery_email_sent_at)}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-neutral-500">
                          —
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleRecover(it.booking_id)}
                        disabled={acting[it.booking_id]}
                        className="bg-terracotta hover:bg-terracotta/90 whitespace-nowrap"
                        data-testid={`abandoned-recover-${it.booking_id}`}
                      >
                        {acting[it.booking_id] ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        {it.recovery_email_sent_at ? "Reenviar" : "Enviar recordatorio"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ testid, icon: Icon, colorClass, value, label }) {
  return (
    <div className="card-base flex items-start gap-3" data-testid={testid}>
      <div className={`w-10 h-10 ${colorClass} rounded-xl flex items-center justify-center shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-heading font-bold">{value}</p>
        <p className="text-sm text-gray-500 leading-tight">{label}</p>
      </div>
    </div>
  );
}

function fmtAge(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const min = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    if (min < 60) return `hace ${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `hace ${hr}h`;
    const days = Math.floor(hr / 24);
    return `hace ${days}d`;
  } catch (_e) {
    return iso;
  }
}
